/**
 * SENTRY INTEGRATION - Main Configuration
 *
 * Purpose: Central configuration file for Sentry error tracking.
 * This file initializes Sentry and provides helper functions used across the app.
 *
 * How it works in Sentry process:
 * - initSentry(): Called once at app startup (in index.js) to initialize error tracking
 * - setSentryUser(): Called when user logs in to attach user info to errors
 * - clearSentryUser(): Called when user logs out to remove user context
 * - addSentryBreadcrumb(): Records user actions to help debug errors
 * - captureException(): Manually sends errors to Sentry (used in axios for API errors)
 *
 * Environment variables required:
 * - REACT_APP_SENTRY_DSN: Your Sentry project DSN
 * - REACT_APP_ENVIRONMENT: development | staging | production
 * - REACT_APP_VERSION: App version for release tracking
 */
import * as Sentry from "@sentry/react";
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || "development";
const RELEASE_VERSION = process.env.REACT_APP_VERSION || "1.0.0";

const isSentryEnabled = () => {
  if (!SENTRY_DSN) {
    return false;
  }

  if (ENVIRONMENT === "production" || ENVIRONMENT === "staging") {
    return true;
  }

  return process.env.REACT_APP_SENTRY_ENABLE_DEV === "true";
};

const getSampleRates = () => {
  switch (ENVIRONMENT) {
    case "production":
      return {
        tracesSampleRate: 0.1, // 10% of transactions
        replaysSessionSampleRate: 0.01, // 1% of sessions
        replaysOnErrorSampleRate: 0.5, // 50% of sessions with errors
      };
    case "staging":
      return {
        tracesSampleRate: 0.5, // 50% of transactions
        replaysSessionSampleRate: 0.1, // 10% of sessions
        replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      };
    default:
      return {
        tracesSampleRate: 1.0, // 100% in development
        replaysSessionSampleRate: 0.0, // No replays in dev
        replaysOnErrorSampleRate: 0.0,
      };
  }
};

export const initSentry = () => {
  if (!isSentryEnabled()) return;

  const sampleRates = getSampleRates();

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `handyday-web@${RELEASE_VERSION}`,
    sendDefaultPii: true,

    tracesSampleRate: sampleRates.tracesSampleRate,

    replaysSessionSampleRate: sampleRates.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sampleRates.replaysOnErrorSampleRate,

    integrations: [
      Sentry.browserTracingIntegration({
        // Set to capture interactions like clicks, inputs
        enableInp: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.httpClientIntegration(),
    ],

    beforeSend(event, hint) {
      const error = hint.originalException;

      if (error?.message?.includes("Network Error")) {
        return null;
      }

      if (error?.message?.includes("canceled")) {
        return null;
      }

      if (error?.message?.includes("ResizeObserver")) {
        return null;
      }

      return event;
    },

    beforeSendTransaction(event) {
      // Remove sensitive query parameters from URLs
      if (event.request?.url) {
        const url = new URL(event.request.url);
        // this list can be increased in future
        const sensitiveParams = ["token", "password", "secret", "key", "auth"];
        sensitiveParams.forEach((param) => {
          if (url.searchParams.has(param)) {
            url.searchParams.set(param, "[FILTERED]");
          }
        });
        event.request.url = url.toString();
      }
      return event;
    },
    normalizeDepth: 5,
    debug: ENVIRONMENT === "development" && process.env.REACT_APP_SENTRY_DEBUG === "true",
  });
};

export const setSentryUser = (user) => {
  if (!isSentryEnabled() || !user) {
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    ...(user.ActiveOrganization && {
      organizationId: user.ActiveOrganization.id,
      organizationName: user.ActiveOrganization.name,
    }),
  });
};

export const clearSentryUser = () => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.setUser(null);
};

export const addSentryBreadcrumb = (message, category = "user-action", data = {}) => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level: "info",
    data,
  });
};

export const captureException = (error, context = {}) => {
  if (!isSentryEnabled()) return;

  Sentry.captureException(error, {
    extra: context,
  });
};

export const captureMessage = (message, level = "info", context = {}) => {
  if (!isSentryEnabled()) return;

  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

export {Sentry};